import Subtitle from "../../text/Subtitle/Subtitle";
import Title from "../../text/Title/Title";
import { cn } from "@/utils/cnHelper";
import type { IconDefinition } from "@icons/regular";
import { Icon } from "@ui";
import * as React from "react";

type BaseFeatureShowcaseProps = {
  meta?: string;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  features?: {
    name: string;
    description?: string;
    icon?: IconDefinition;
  }[];
  imagePlacement?: "left" | "right";
  borderedImage?: boolean;
  borderColor?: string; // e.g., "grey_scale-900"
  preventOverflow?: boolean;
};

type ImageFeatureShowcaseProps = BaseFeatureShowcaseProps & {
  image: string;
  video?: never;
};

type VideoFeatureShowcaseProps = BaseFeatureShowcaseProps & {
  video: string;
  image?: never;
};

export type FeatureShowcaseProps = ImageFeatureShowcaseProps | VideoFeatureShowcaseProps;

export default function FeatureShowcase({
  meta,
  title,
  description,
  features,
  imagePlacement = "left",
  image,
  video,
  borderedImage = false,
  borderColor = "#eee",
  preventOverflow = false,
}: FeatureShowcaseProps) {
  return (
    <div
      className={cn(
        "tw-mx-auto tw-grid tw-max-w-2xl tw-grid-cols-1 tw-items-center tw-gap-x-6 tw-gap-y-10 sm:tw-gap-y-16 lg:tw-mx-0 lg:tw-max-w-none lg:tw-gap-y-20",
        preventOverflow ? "lg:tw-grid-cols-3" : "lg:tw-grid-cols-2"
      )}
    >
      <div
        className={cn(
          "lg:tw-pt-4",
          preventOverflow ? "lg:tw-col-span-1" : "",
          imagePlacement === "right" && "lg:tw-pl-0 lg:tw-pr-2",
          imagePlacement === "left" && "lg:tw-pl-2 lg:tw-pr-0"
        )}
      >
        <div className="lg:tw-max-w-md">
          {meta && <div className="tw-text-sm/8 tw-font-semibold tw-uppercase tw-text-brand_scale-400 sm:tw-text-base/8">{meta}</div>}
          {title && (
            <Title as="h3" className="tw-mt-2">
              {title}
            </Title>
          )}
          {description && (
            <Subtitle variant="feature" className="tw-mt-6">
              {description}
            </Subtitle>
          )}
          {features && (
            <dl className="tw-mt-10 tw-max-w-xl tw-space-y-8 tw-text-balance tw-text-base/7 tw-text-[var(--foreground-color-muted)] lg:tw-max-w-[40ch]">
              {features.map(feature => (
                <div key={feature.name} className={cn("tw-relative", feature.icon && "tw-pl-9")}>
                  <dt className="tw-inline tw-font-semibold tw-text-[var(--foreground-color)]">
                    {feature.icon && (
                      <Icon
                        icon={feature.icon}
                        aria-hidden="true"
                        className="tw-absolute tw-left-1 tw-top-1 tw-size-5 tw-text-brand_scale-500"
                      />
                    )}
                    {feature.name}
                  </dt>
                  <dd className="tw-block">{feature.description}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
      <div
        className={cn(
          preventOverflow ? "lg:tw-col-span-2" : "",
          imagePlacement === "left" && "tw-flex tw-items-start tw-justify-end lg:tw-order-first"
        )}
        {...(borderedImage ? { style: { "--feature-border-color": borderColor } as React.CSSProperties } : {})}
      >
        {borderedImage ? (
          <div className="tw-w-fit tw-rounded-2xl tw-bg-[var(--feature-border-color)] tw-p-4 tw-shadow-xl tw-ring-1 tw-ring-inset tw-ring-white/10 md:tw-py-8 lg:tw--m-4 lg:tw-p-12">
            {video && (
              <video
                autoPlay
                loop
                muted
                playsInline
                className={cn(
                  preventOverflow
                    ? "tw-h-auto tw-w-full tw-rounded-xl tw-object-contain tw-shadow-xl"
                    : "tw-w-[48rem] tw-max-w-none tw-rounded-xl tw-shadow-xl sm:tw-w-[57rem]",
                  !preventOverflow && imagePlacement === "right" && "md:tw-ml-4 lg:tw-ml-0"
                )}
              >
                <source src={`../../../${video}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
            {image && (
              <img
                alt="Product screenshot"
                src={`../../../${image}`}
                width={2432}
                height={1442}
                className={cn(
                  preventOverflow
                    ? "tw-h-auto tw-w-full tw-rounded-xl tw-object-contain tw-shadow-xl"
                    : "tw-w-[48rem] tw-max-w-none tw-rounded-xl tw-shadow-xl sm:tw-w-[57rem]",
                  !preventOverflow && imagePlacement === "right" && "md:tw-ml-4 lg:tw-ml-0"
                )}
              />
            )}
          </div>
        ) : (
          <>
            {video && (
              <video
                autoPlay
                loop
                muted
                playsInline
                className={cn(
                  preventOverflow
                    ? "tw-h-auto tw-w-full tw-rounded-xl tw-object-contain tw-shadow-xl"
                    : "tw-w-[48rem] tw-max-w-none tw-rounded-xl tw-shadow-xl tw-ring-1 tw-ring-white/10 sm:tw-w-[57rem]",
                  !preventOverflow && imagePlacement === "right" && "md:tw-ml-4 lg:tw-ml-0"
                )}
              >
                <source src={`../../../${video}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
            {image && (
              <img
                alt="Product screenshot"
                src={`../../../${image}`}
                width={2432}
                height={1442}
                className={cn(
                  preventOverflow
                    ? "tw-h-auto tw-w-full tw-rounded-xl tw-object-contain tw-shadow-xl"
                    : "tw-w-[48rem] tw-max-w-none tw-rounded-xl tw-shadow-xl tw-ring-1 tw-ring-white/10 sm:tw-w-[57rem]",
                  !preventOverflow && imagePlacement === "right" && "md:tw-ml-4 lg:tw-ml-0"
                )}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
